import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { listingTableSx } from './listingTableSx';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Tabs,
  Tab,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Stack,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  Email as EmailIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Support as SupportIcon,
  AccessTime as HoursIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Groups as GroupsIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { teamContactsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { canManageTeamType, canAccessRoute } from '../utils/rolePermissions';
import CenteredLoader from './CenteredLoader';
import { ListingSortSelect } from './ListingPagination';
import { UI_TOKENS } from '../constants/uiTokens';
import { PAGE, CARD_SX, TABS_SX } from '../constants/pageStyles';
import { DEFAULT_LISTING_SORT, ListingSortValue, sortListing } from '../utils/listingSort';

const TEAM_TYPES = ['SSO', 'PAM', 'IGA'] as const;
type TeamType = (typeof TEAM_TYPES)[number];

interface TeamContact {
  id: number | string;
  team_type: TeamType;
  user_id: string;
  name: string;
  email: string;
  role: string;
  source?: 'assigned' | 'role';
  created_at: string | null;
  updated_at: string | null;
}

interface TeamInfo {
  id: number;
  team_type: TeamType;
  servicenow_queue_name?: string | null;
  email_dg?: string | null;
  coverage_hours?: string | null;
  created_at: string;
  updated_at: string;
}

const TEAM_DESCRIPTIONS: Record<TeamType, string> = {
  SSO: 'Single Sign-On — authentication, federation, and access requests',
  PAM: 'Privileged Access Management — elevated access and vault requests',
  IGA: 'Identity Governance — entitlements, access reviews, and compliance',
};

const TEAM_CHIP_SX: Record<TeamType, { bgcolor: string; color: string; border?: string }> = {
  SSO: { bgcolor: '#1565c0', color: '#fff' },
  PAM: { bgcolor: '#ed6c02', color: '#fff' },
  IGA: { bgcolor: '#2e7d32', color: '#fff' },
};

const teamChipSx = (teamType: TeamType) => ({
  ...TEAM_CHIP_SX[teamType],
  fontWeight: 700,
});

const TeamContactsView: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [contacts, setContacts] = useState<TeamContact[]>([]);
  const [teamInfo, setTeamInfo] = useState<Partial<Record<TeamType, TeamInfo>>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [listingSort, setListingSort] = useState<ListingSortValue>(DEFAULT_LISTING_SORT);
  const [selectedContact, setSelectedContact] = useState<TeamContact | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const enterpriseLabel =
    (user as { enterprise_name?: string } | null)?.enterprise_name?.trim() || 'Your organization';

  const canOpenSettings =
    user?.role != null && canAccessRoute(user.role, '/settings');

  const loadData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const contactResult = await teamContactsAPI.getAllContacts().catch((err) => {
        console.error('Error loading contacts:', err);
        return null;
      });

      const teamInfoRows = await Promise.all(
        TEAM_TYPES.map((teamType) =>
          teamContactsAPI.getTeamInfo(teamType).catch((err) => {
            console.error(`Error loading ${teamType} team info:`, err);
            return null;
          })
        )
      );

      if (contactResult == null) {
        setError('Failed to load team contacts. Please try again.');
        setContacts([]);
      } else {
        setContacts(contactResult || []);
      }

      const infoMap: Partial<Record<TeamType, TeamInfo>> = {};
      TEAM_TYPES.forEach((teamType, index) => {
        const row = teamInfoRows[index];
        if (row) infoMap[teamType] = row;
      });
      setTeamInfo(infoMap);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('Error loading team contacts:', err);
      setError('Failed to load team contacts. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const activeTeamFilter: TeamType | null = selectedTab === 0 ? null : TEAM_TYPES[selectedTab - 1];

  const visibleTeamTypes = useMemo((): TeamType[] => {
    if (activeTeamFilter) return [activeTeamFilter];
    return [...TEAM_TYPES];
  }, [activeTeamFilter]);

  const teamCounts = useMemo(() => {
    const counts: Record<TeamType, number> = { SSO: 0, PAM: 0, IGA: 0 };
    for (const c of contacts) {
      counts[c.team_type] += 1;
    }
    return counts;
  }, [contacts]);

  const filteredContacts = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    const filtered = contacts.filter((contact) => {
      if (activeTeamFilter && contact.team_type !== activeTeamFilter) return false;
      if (!q) return true;
      return (
        contact.name?.toLowerCase().includes(q) ||
        contact.email?.toLowerCase().includes(q) ||
        contact.role?.toLowerCase().includes(q) ||
        contact.team_type.toLowerCase().includes(q)
      );
    });
    return sortListing(filtered, listingSort);
  }, [contacts, activeTeamFilter, searchText, listingSort]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleContactClick = (contact: TeamContact) => {
    setSelectedContact(contact);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedContact(null);
  };

  const renderTeamInfoValue = (value?: string | null, mailto = false) => {
    const text = value?.trim() || 'Not configured';
    if (!value?.trim()) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2, fontStyle: 'italic' }}>
          {text}
        </Typography>
      );
    }
    if (mailto) {
      return (
        <Link href={`mailto:${value.trim()}`} variant="body2" sx={{ ml: 2 }} underline="hover">
          {text}
        </Link>
      );
    }
    return (
      <Typography variant="body2" sx={{ ml: 2 }}>
        {text}
      </Typography>
    );
  };

  if (loading) {
    return (
      <CenteredLoader size={28} />
    );
  }

  return (
    <Box sx={PAGE.padding}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.25} sx={{ mb: 2.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
            color: 'primary.main',
          }}
        >
          <GroupsIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={PAGE.title}>IAM Team Contacts</Typography>
          <Typography sx={PAGE.subtitle}>
            Live directory for {enterpriseLabel} — SSO, PAM, and IGA contacts
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {canOpenSettings && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<SettingsIcon sx={{ fontSize: '0.9rem !important' }} />}
              onClick={() => navigate('/settings?section=team-contacts')}
              sx={{ fontSize: UI_TOKENS.text.buttonSm }}
            >
              Manage in Settings
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon sx={{ fontSize: '0.9rem !important' }} />}
            onClick={() => void loadData(true)}
            disabled={refreshing}
            sx={{ fontSize: UI_TOKENS.text.buttonSm }}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {lastRefreshed && (
        <Typography sx={{ ...PAGE.helper, mb: 2 }}>
          Last updated {lastRefreshed.toLocaleString()}
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined" sx={{ borderRadius: 1.5 }}>
            <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
              <Typography sx={PAGE.helper}>Total contacts</Typography>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700 }}>{contacts.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        {TEAM_TYPES.map((teamType) => (
          <Grid item xs={6} sm={3} key={teamType}>
            <Card variant="outlined" sx={{ borderRadius: 1.5 }}>
              <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Chip label={teamType} size="small" sx={{ mb: 0.5, ...teamChipSx(teamType) }} />
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 700 }}>{teamCounts[teamType]}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card variant="outlined" sx={CARD_SX}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover', px: 1.5 }}>
          <Tabs value={selectedTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" sx={TABS_SX}>
            <Tab label={`All Teams (${contacts.length})`} />
            {TEAM_TYPES.map((teamType) => (
              <Tab key={teamType} label={`${teamType} (${teamCounts[teamType]})`} />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              placeholder="Search by name, email, role, or team…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ maxWidth: 420, flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <ListingSortSelect value={listingSort} onChange={setListingSort} />
          </Stack>

          <Typography sx={{ ...PAGE.sectionTitle, mb: 1.5 }}>
            {activeTeamFilter ? `${activeTeamFilter} team channels` : 'Team channels'}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {visibleTeamTypes.map((teamType) => (
              <Grid item xs={12} md={activeTeamFilter ? 12 : 4} key={teamType}>
                <Card variant="outlined" sx={{ borderRadius: 1.5, height: '100%' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Chip label={teamType} size="small" sx={teamChipSx(teamType)} />
                      <Typography sx={PAGE.helper}>{teamCounts[teamType]} contact{teamCounts[teamType] === 1 ? '' : 's'}</Typography>
                    </Stack>
                    <Typography sx={{ ...PAGE.helper, mb: 1.5 }}>{TEAM_DESCRIPTIONS[teamType]}</Typography>
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <SupportIcon color="action" fontSize="small" />
                        <Typography variant="caption" color="text.secondary">
                          ServiceNow queue
                        </Typography>
                      </Box>
                      {renderTeamInfoValue(teamInfo[teamType]?.servicenow_queue_name)}
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <EmailIcon color="action" fontSize="small" />
                        <Typography variant="caption" color="text.secondary">
                          Team email
                        </Typography>
                      </Box>
                      {renderTeamInfoValue(teamInfo[teamType]?.email_dg, true)}
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <HoursIcon color="action" fontSize="small" />
                        <Typography variant="caption" color="text.secondary">
                          Coverage hours
                        </Typography>
                      </Box>
                      {renderTeamInfoValue(teamInfo[teamType]?.coverage_hours)}
                    </Box>
                    {canManageTeamType(user?.role, teamType) && canOpenSettings && (
                      <Button
                        size="small"
                        sx={{ mt: 1.5, fontSize: UI_TOKENS.text.buttonSm }}
                        onClick={() =>
                          navigate(
                            `/settings?section=team-contacts&team=${teamType}&edit=team-info`
                          )
                        }
                      >
                        Configure {teamType} team info
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Typography sx={{ ...PAGE.sectionTitle, mb: 1 }}>
            Queues and people ({filteredContacts.length})
          </Typography>
          <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary', mb: 1 }}>
            A queue is a shared address. A person is a named admin. Test domains stay marked so they are not treated as production contacts.
          </Typography>
          <TableContainer>
            <Table size="small" sx={listingTableSx}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  {['Name', 'Email', 'Role', 'Team', 'Source', ''].map((label) => (
                    <TableCell key={label || 'actions'} sx={{ fontWeight: 700, fontSize: UI_TOKENS.text.bodySm }}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredContacts.map((contact, index) => (
                  <TableRow
                    key={`${contact.team_type}-${contact.user_id}-${contact.id ?? index}`}
                    hover
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="action" fontSize="small" />
                        <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }}>
                          {contact.name || '—'}
                        </Typography>
                        <Chip
                          size="small"
                          label={/queue/i.test(`${contact.name || ''} ${contact.email || ''}`) ? 'Queue' : 'Person'}
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                        {/@(example\.com|mailinator\.com)$/i.test(contact.email || '') || /\.test$/i.test(contact.email || '') ? (
                          <Chip size="small" label="Test domain" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {contact.email ? (
                        <Link href={`mailto:${contact.email}`} underline="hover" sx={{ fontSize: UI_TOKENS.text.bodySm }}>
                          {contact.email}
                        </Link>
                      ) : (
                        <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }}>{contact.role || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={contact.team_type || '—'}
                        size="small"
                        sx={teamChipSx((contact.team_type as TeamType) || 'SSO')}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={contact.source === 'role' ? 'Platform admin' : 'Assigned'}
                        size="small"
                        variant="outlined"
                        color={contact.source === 'role' ? 'default' : 'success'}
                        sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View details">
                        <IconButton size="small" onClick={() => handleContactClick(contact)}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredContacts.length === 0 && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary" sx={{ fontSize: UI_TOKENS.text.bodySm }}>
                {contacts.length === 0
                  ? 'No team contacts or platform admins found for this organization yet.'
                  : 'No contacts match your search or selected team.'}
              </Typography>
              {contacts.length === 0 && canOpenSettings && (
                <Button
                  size="small"
                  sx={{ mt: 1.5 }}
                  onClick={() => navigate('/settings?section=team-contacts')}
                >
                  Add contacts in Settings
                </Button>
              )}
              {contacts.some((c) => c.source === 'role') && canOpenSettings && (
                <Typography sx={{ ...PAGE.helper, mt: 1.5 }}>
                  Platform admins appear automatically. Promote them to assigned contacts in Settings → Team Contacts.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 700 }}>Contact details</DialogTitle>
        <DialogContent>
          {selectedContact && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  <PersonIcon color="primary" fontSize="small" />
                  <Typography sx={{ fontWeight: 600 }}>{selectedContact.name}</Typography>
                  <Chip label={selectedContact.team_type} size="small" sx={teamChipSx(selectedContact.team_type)} />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Typography sx={PAGE.helper}>{TEAM_DESCRIPTIONS[selectedContact.team_type]}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }}>
                  <strong>Email:</strong>{' '}
                  <Link href={`mailto:${selectedContact.email}`}>{selectedContact.email}</Link>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }}>
                  <strong>Role:</strong> {selectedContact.role}
                </Typography>
              </Grid>
              {teamInfo[selectedContact.team_type]?.servicenow_queue_name && (
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }}>
                    <strong>ServiceNow queue:</strong> {teamInfo[selectedContact.team_type]?.servicenow_queue_name}
                  </Typography>
                </Grid>
              )}
              {teamInfo[selectedContact.team_type]?.email_dg && (
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }}>
                    <strong>Team email:</strong>{' '}
                    <Link href={`mailto:${teamInfo[selectedContact.team_type]?.email_dg}`}>
                      {teamInfo[selectedContact.team_type]?.email_dg}
                    </Link>
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamContactsView;
